import {
  Dispatch,
  JSX,
  SetStateAction,
  createContext,
  useMemo,
  useState,
} from 'react';
import useMount from '../../../common/hooks/useMount';
import { Category } from '../models/Categories';
import { categoryService } from '../services/category';

export interface IBenefitsContext {
  flexibleBenefitsCategoryRoot?: Category;
  benefitClubCategoryRoot?: Category;
  cashbackCategoryRoot?: Category;
  discountVouchersCategoryRoot?: Category;
}

interface BenefitsContextValue {
  benefitRoots: IBenefitsContext;
  setBenefitRoots: Dispatch<SetStateAction<IBenefitsContext>>;
}

export const BenefitsContext = createContext({} as BenefitsContextValue);

export function BenefitsProvider(props: { children: JSX.Element }) {
  const [benefitRoots, setBenefitRoots] = useState<IBenefitsContext>({});

  const loadBenefitRoots = (abortController?: AbortController) => {
    const axiosConfig = {
      signal: abortController?.signal,
    };

    const flexibleBenefitsRequest = categoryService
      .getFlexibleBenefits(axiosConfig)
      .then(result => {
        if (result.hasErrors) {
          return;
        }

        setBenefitRoots(current => ({
          ...current,
          flexibleBenefitsCategoryRoot: result.data,
        }));
      });

    const benefitClubRequest = categoryService
      .getClubBenefits(axiosConfig)
      .then(result => {
        if (result.hasErrors) {
          return;
        }

        setBenefitRoots(current => ({
          ...current,
          benefitClubCategoryRoot: result.data,
        }));
      });

    const discountVouchers = categoryService
      .getDiscountVouchers(axiosConfig)
      .then(result => {
        if (result.hasErrors) {
          return;
        }

        setBenefitRoots(current => ({
          ...current,
          discountVouchersCategoryRoot: result.data,
        }));
      });

    return Promise.allSettled([
      flexibleBenefitsRequest,
      benefitClubRequest,
      discountVouchers,
    ]);
  };

  useMount(loadBenefitRoots);

  const contextValue = useMemo(
    () => ({
      benefitRoots,
      setBenefitRoots,
    }),
    [benefitRoots],
  );

  return (
    <BenefitsContext.Provider value={contextValue}>
      {props.children}
    </BenefitsContext.Provider>
  );
}
